import _toConsumableArray from "/builds/client-projects/fert-demolition-motos-website/application/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/builds/client-projects/fert-demolition-motos-website/application/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/builds/client-projects/fert-demolition-motos-website/application/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import { isEntity } from "./entities/Entity.js";
import { DELETED } from "./special.js";
import WeakListMap from "./WeakListMap.js";
import { denormalize as arrayDenormalize } from "./schemas/Array.js";
import { denormalize as objectDenormalize } from "./schemas/Object.js";
import { isImmutable } from "./schemas/ImmutableUtils.js";
var DRAFT = Symbol('draft');

var unvisitEntity = function unvisitEntity(id, schema, unvisit, getEntity, localCache, entityCache, dependencies, cycleIndex) {
  var entity = getEntity(id, schema);

  if (entity === DELETED) {
    return [undefined, true, true];
  }

  if (process.env.NODE_ENV !== 'production' && typeof entity === 'symbol' && entity.toString().includes('DELETED')) {
    throw new Error("Unrecognized symbol detected.\nMake sure you do not have multiple versions of @rest-hooks/normalizr installed.");
  }

  if (typeof entity !== 'object' || entity === null) {
    return [entity, false, false];
  }

  if (localCache[schema.key] === undefined) {
    localCache[schema.key] = {};
  }

  var found = true;
  var deleted = false;

  if (!localCache[schema.key][id]) {
    var trackingIndex = dependencies.length;
    dependencies.push(entity);
    var wrappedUnvisit = withTrackedEntities(unvisit); // { [DRAFT] } means we are still processing - which if found indicates a cycle

    wrappedUnvisit.setLocal = function (entityCopy) {
      var _localCache$schema$ke;

      return localCache[schema.key][id] = (_localCache$schema$ke = {}, _defineProperty(_localCache$schema$ke, DRAFT, entityCopy), _defineProperty(_localCache$schema$ke, "i", trackingIndex), _localCache$schema$ke);
    };

    var globalCacheEntry = getGlobalCacheEntry(entityCache, schema, id);

    var _schema$denormalize = schema.denormalize(entity, wrappedUnvisit);

    var _schema$denormalize2 = _slicedToArray(_schema$denormalize, 3);

    localCache[schema.key][id] = _schema$denormalize2[0];
    found = _schema$denormalize2[1];
    deleted = _schema$denormalize2[2];
    // if in cycle, use the start of the cycle to track all deps
    // otherwise, we use our own trackingIndex
    var localKey = dependencies.slice(cycleIndex.i === -1 ? trackingIndex : cycleIndex.i);

    if (!globalCacheEntry.has(localKey)) {
      globalCacheEntry.set(localKey, localCache[schema.key][id]);
    } else {
      localCache[schema.key][id] = globalCacheEntry.get(localKey);
    } // start of cycle - reset cycle detection


    if (cycleIndex.i === trackingIndex) {
      cycleIndex.i = -1;
    }
  } else {
    // cycle detected
    if (Object.prototype.hasOwnProperty.call(localCache[schema.key][id], DRAFT)) {
      cycleIndex.i = localCache[schema.key][id].i;
      return [localCache[schema.key][id][DRAFT], found, deleted];
    } else {
      // with no cycle, globalCacheEntry will have already been set
      dependencies.push(entity);
    }
  }

  return [localCache[schema.key][id], found, deleted];
};

var getUnvisit = function getUnvisit(entities, entityCache, resultCache, localCache) {
  var getEntity = getEntities(entities);
  var dependencies = [];
  var cycleIndex = {
    i: -1
  };

  function unvisit(input, schema) {
    if (!schema) return [input, true, false];

    if (!schema.denormalize || typeof schema.denormalize !== 'function') {
      if (typeof schema === 'function') {
        if (input instanceof schema) return [input, true, false];
        return [new schema(input), true, false];
      } else if (typeof schema === 'object') {
        var method = Array.isArray(schema) ? arrayDenormalize : objectDenormalize;
        return method(schema, input, unvisit);
      }
    } // null is considered intentional, thus always 'found' as true


    if (input === null) {
      return [input, true, false];
    }

    if (isEntity(schema)) {
      // unvisitEntity just can't handle undefined
      if (input === undefined) {
        return [input, false, false];
      }

      return unvisitEntity(input, schema, unvisit, getEntity, localCache, entityCache, dependencies, cycleIndex);
    }

    if (typeof schema.denormalize === 'function') {
      return schema.denormalize(input, unvisit);
    }

    return [input, true, false];
  } //const wrappedUnvisit = withTrackedEntities(unvisit, globalKey);


  return function (input, schema) {
    var ret = unvisit(input, schema); // in the case where WeakMap cannot be used
    // this test ensures null is properly excluded from WeakMap

    if (Object(input) !== input) return ret;
    dependencies.push(input);

    if (!resultCache.has(dependencies)) {
      resultCache.set(dependencies, ret[0]);
      return ret;
    } else {
      return [resultCache.get(dependencies), ret[1], ret[2]];
    }
  };
};

var getEntities = function getEntities(entities) {
  var entityIsImmutable = isImmutable(entities);
  return function (entityOrId, schema) {
    var _entities$schemaKey;

    var schemaKey = schema.key;

    if (typeof entityOrId === 'object') {
      return entityOrId;
    }

    if (entityIsImmutable) {
      return entities.getIn([schemaKey, entityOrId]);
    }

    return (_entities$schemaKey = entities[schemaKey]) == null ? void 0 : _entities$schemaKey[entityOrId];
  };
}; // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types


export var denormalize = function denormalize(input, schema, entities) {
  var entityCache = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var resultCache = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : new WeakListMap();

  // undefined mean don't do anything
  if (schema === undefined) {
    return [input, true, false, {}];
  }

  if (input === undefined) {
    return [undefined, false, false, {}];
  }

  var resolvedEntities = {};
  var unvisit = getUnvisit(entities, entityCache, resultCache, resolvedEntities);
  return [].concat(_toConsumableArray(unvisit(input, schema)), [resolvedEntities]);
};
export var denormalizeSimple = function denormalizeSimple(input, schema, entities) {
  var entityCache = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var resultCache = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : new WeakListMap();
  return denormalize(input, schema, entities, entityCache, resultCache).slice(0, 3);
};

function getGlobalCacheEntry(entityCache, schema, id) {
  if (!entityCache[schema.key]) entityCache[schema.key] = {};
  if (!entityCache[schema.key][id]) entityCache[schema.key][id] = new WeakListMap();
  return entityCache[schema.key][id];
}

function withTrackedEntities(unvisit) {
  // every time we nest, we want to unwrap back to the top.
  // this is due to only needed the next level of nested entities for lookup
  var originalUnvisit = unvisit.og || unvisit;

  var wrappedUnvisit = function wrappedUnvisit(input, schema) {
    return originalUnvisit(input, schema);
  };

  wrappedUnvisit.og = unvisit;
  return wrappedUnvisit;
}